import type { RegulatoryMode } from '@allergen-lens/shared-logic';
import type { CrossContactRisk } from '@allergen-lens/shared-logic';

export interface DemoDataset {
  name: string;
  description: string;
  rawInput: string;
  mode: RegulatoryMode;
  crossContactRisks?: CrossContactRisk[];
}

/**
 * Dataset count justification: 5 datasets
 *
 * The engine has these distinct result paths:
 * 1. Exact/synonym match → Present confidence
 * 2. Derived match → Possible/NeedsReview confidence
 * 3. Ambiguous terms ("natural flavor", "spices") → NeedsReview
 * 4. Compound ingredient expansion (e.g. pesto → basil, pine nuts, parmesan)
 * 5. PAL statement detection ("may contain...")
 * 6. Species resolution (tree nuts, fish, crustaceans)
 * 7. EU vs US regulatory mode (different allergen sets + declaration formats)
 * 8. Zero allergens detected → clean/empty result path
 *
 * Coverage:
 * - Dataset 1 (Simple EU Snack): paths 1, 7 (EU mode), basic happy path
 * - Dataset 2 (Complex EU Product): paths 1, 3, 4, 5, 6, 7 — full pipeline stress test
 * - Dataset 3 (US Product): paths 1, 6, 7 (US mode), US declaration generation
 * - Dataset 4 (Ambiguous Recipe): paths 2, 3 — exercises NeedsReview/derived matching
 * - Dataset 5 (Clean Recipe): path 8 — zero-detection boundary case
 *
 * No additional datasets needed: the 5 above cover every engine branch.
 */

export const demoDatasets: DemoDataset[] = [
  {
    name: 'Simple EU Snack',
    description: 'A short ingredient list with milk and wheat allergens — exercises basic exact-match detection in EU mode.',
    rawInput: 'Wheat flour, Butter, Sugar, Egg, Milk, Baking powder, Vanilla extract',
    mode: 'EU' as RegulatoryMode,
  },
  {
    name: 'Complex EU Product',
    description: 'A multi-component product with compound ingredients, PAL statements, and species-level resolution. Exercises the full engine pipeline.',
    rawInput: 'Pasta (durum wheat, egg), Pesto (basil, pine nuts, parmesan, garlic), Caesar dressing (anchovy, egg yolk, parmesan), Shrimp, Worcestershire sauce. May contain traces of peanuts and tree nuts. Produced in a facility that processes milk, eggs, and fish.',
    mode: 'EU' as RegulatoryMode,
    crossContactRisks: [
      {
        id: 'ccr-demo-1',
        equipment: 'Shared mixing line',
        facility: 'Main production facility',
        allergens: ['milk', 'eggs', 'fish'],
        riskLevel: 'medium',
        notes: 'Shared equipment cleaned between runs per SOP-CC-01',
      },
    ],
  },
  {
    name: 'US Packaged Food',
    description: 'A typical US-packaged food with US-specific allergens. Tests US regulatory mode and FALCPA declaration format.',
    rawInput: 'Enriched wheat flour, Soy lecithin, Milk chocolate (sugar, cocoa butter, milk, soy lecithin), Peanuts, Almonds, Sesame seeds, Egg white powder',
    mode: 'US' as RegulatoryMode,
  },
  {
    name: 'Ambiguous Asian Stir-Fry',
    description: 'Contains ambiguous terms like "natural flavor" and "spices" that trigger NeedsReview. Also exercises derived matching.',
    rawInput: 'Rice noodles, Soy sauce, Fish sauce, Oyster sauce, Tofu, Shrimp, Natural flavor, Spices, Vegetable oil, Sesame oil, Chili powder, Garlic, Ginger',
    mode: 'EU' as RegulatoryMode,
  },
  {
    name: 'Clean Fruit Salad',
    description: 'A simple recipe with no detectable allergens. Tests the zero-detection / clean-result path.',
    rawInput: 'Strawberries, Blueberries, Raspberries, Banana, Mango, Pineapple, Honey, Lime juice, Mint leaves',
    mode: 'EU' as RegulatoryMode,
  },
];
